"use client"

import type React from "react"
import { useState, useEffect, useMemo } from "react"
import { motion } from "framer-motion"
import { ConversationMode, InterViewCurrentTask, InterviewInitState } from "../../types/interview.type"
import { useInterview } from "../../contexts/interview.context"
import { useAppSelector } from "../../redux/hooks"
import { useTheme } from "../../context"
import VideoPreview from "./VideoPreview"
import InterviewVideo from "./Video"
import { Clock } from "lucide-react"
import TypingDots from "./TypingDots"
import { SessionStorage } from "../../types/general.type"
import { VideoToggleButton } from "./video-toggle-btn"

interface InterviewCardProps {
  showChat: boolean
  toggleChat: () => void
  handleClose: () => void
}

const InterviewCard: React.FC<InterviewCardProps> = ({ showChat, toggleChat, handleClose }) => {
  const { theme } = useTheme()
  const [showVideo, setShowVideo] = useState(true)

  const user = useAppSelector((state) => state.auth.user)
  const { interview, updateInterview } = useInterview()
  const {
    isMuted,
    isVideoOff,
    currentTask,
    setUp: interviewSetup,
    showProcessing,
    selectedLanguage
  } = interview
  const state = JSON.parse(sessionStorage.getItem(SessionStorage.INTERVIEW_STATE) || "{}") as InterviewInitState;
  const { selectedInterviweer } = state || {}
  const [cameraAllowed, setCameraAllowed] = useState<boolean>(false);
  useEffect(() => {
    if (!selectedLanguage) {
      updateInterview({ selectedLanguage: "en-US" })
    }
  }, [selectedLanguage, updateInterview])

  useEffect(() => {
    isCameraAllowed();
  }, []);

  async function isCameraAllowed() {
    try {
      const status = await navigator.permissions.query({ name: 'camera' });
      if (status.state === 'granted') {
        setCameraAllowed(true);
      } else if (status.state === 'denied') {
        setCameraAllowed(false);
      } else {
        // If permission is not granted or denied, try to access the camera
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop()); // stop stream after test
        setCameraAllowed(true);
      }
    } catch {
      // Fallback: try to access the camera directly
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop()); // stop stream after test
        setCameraAllowed(true);
      } catch {
        setCameraAllowed(false);
      }
    }
  }

  const interviewerTasks = [
    InterViewCurrentTask.BOT_SPEAKING,
    InterViewCurrentTask.PROCESSING_ANSWER,
    InterViewCurrentTask.BOT_TYPING,
  ]
  const intervieweeTasks = [
    InterViewCurrentTask.YOU_SPEAKING,
    InterViewCurrentTask.WAITING_YOU_FOR_REPLY,
    InterViewCurrentTask.YOU_TYPING,
  ]

  const scrollbarStyles = useMemo(() => `
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: ${theme === "dark" ? "rgba(46,45,50,1)" : "rgba(247,248,250,1)"};
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: ${theme === "dark" ? "rgba(59,130,246,0.7)" : "rgba(59,130,246,0.5)"};
      border-radius: 3px;
      transition: background-color 0.2s;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: ${theme === "dark" ? "rgba(59,130,246,0.9)" : "rgba(59,130,246,0.7)"};
    }
    .custom-scrollbar {
      scrollbar-width: thin;
      scrollbar-color: ${theme === "dark"
      ? "rgba(59,130,246,0.7) rgba(46,45,50,1)"
      : "rgba(59,130,246,0.5) rgba(247,248,250,1)"
    };
    }
    @keyframes border-pulse {
      0% { border-color: ${theme === "dark" ? "rgba(59,130,246,0.3)" : "rgba(59,130,246,0.2)"}; }
      50% { border-color: ${theme === "dark" ? "rgba(59,130,246,0.5)" : "rgba(59,130,246,0.4)"}; }
      100% { border-color: ${theme === "dark" ? "rgba(59,130,246,0.3)" : "rgba(59,130,246,0.2)"}; }
    }
    @keyframes focus-pulse {
      0% { border-color: rgba(8,191,177,0.8); }
      50% { border-color: rgba(8,191,177,1); }
      100% { border-color: rgba(8,191,177,0.8); }
    }
    .animate-border-pulse {
      animation: border-pulse 2s infinite;
    }
    .animate-focus-pulse {
      animation: focus-pulse 1.5s infinite;
    }
  `, [theme])

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className="flex flex-grow flex-col items-center justify-start w-full h-full">
        <div className={`flex flex-col justify-stretch items-stretch h-full w-full gap-${showProcessing ? "0.5" : "2"}`}>
          <div
            className={`relative p-2 sm:p-4 rounded-lg h-[50%] flex justify-center items-center w-full transition-all duration-200 box-border overflow-hidden ${theme === "dark"
              ? "bg-[rgba(46,45,50,1)] border-accent-700/40 border"
              : "bg-[rgba(247,248,250,1)] border-accent-700/40 border"
              } shadow-[0_4px_6px_rgba(0,0,0,0.1),_0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_6px_rgba(0,0,0,0.3),_0_1px_3px_rgba(0,0,0,0.2)] ${currentTask === InterViewCurrentTask.BOT_SPEAKING
                ? "border-[1.5px] xs:border-[2px] border-[#08bfb1] animate-focus-pulse"
                : interviewerTasks.includes(currentTask)
                  ? "border-1"
                  : ""
              }`}
          >
            <div className={`flex justify-center items-center w-full h-full ${(interviewSetup?.conversationMode === ConversationMode.VIDEO && showVideo) ? 'hidden' : "flex"} `}>
              <img
                src={selectedInterviweer?.image ?? "./img1.png"}
                alt="Interviewer"
                // style={{
                //   filter: currentTask === InterViewCurrentTask.BOT_SPEAKING ? "blur(2px)" : "none",
                // }}
                className="w-24 h-24 object-cover rounded-full"
              />
            </div>
            <div className={`w-full h-full relative  justify-center items-center   overflow-hidden ${(interviewSetup?.conversationMode === ConversationMode.VIDEO && showVideo) ? 'flex' : "hidden"}`}>
              {/* <span className="absolute text-xs top-0 p-0.5 px-1 text-white rounded-xl bg-accent-500 z-[2000]">
                Avatar Video Is Beta Version: Turn off video if your internet is slow
              </span> */}
              <InterviewVideo />
            </div>

            <span className={`absolute p-1 text-xs rounded left-1 bottom-1 ${theme === "dark"
              ? "text-gray-100 bg-[rgba(0,0,0,0.5)]"
              : "text-gray-900 bg-[rgba(255,255,255,0.5)]"
              }`}>
              {selectedInterviweer?.name || "Interviewer"}
            </span>
            {currentTask === InterViewCurrentTask.BOT_SPEAKING &&
              (interviewSetup?.conversationMode === ConversationMode.VOICE || !showVideo) && <SoundWave />}

            {interviewSetup?.conversationMode === ConversationMode.VIDEO && (
              <VideoToggleButton
                onToggle={() => setShowVideo(prev => !prev)}
                showVideo={showVideo}
                className="absolute right-[50%] translate-x-[50%] bottom-1"
              />
            )}
          </div>
          {showProcessing && (
            <div className="flex my-0 py-0 items-center justify-center w-full h-[0.5rem]">
              <TypingDots />
            </div>
          )}
          <div
            className={`relative p-2 sm:p-4 rounded-lg h-[50%] flex justify-center items-center w-full transition-all duration-200 box-border overflow-hidden ${theme === "dark"
              ? "bg-[rgba(46,45,50,1)] border-accent-700/40 border"
              : "bg-[rgba(247,248,250,1)] border-accent-700/40 border"
              } shadow-[0_4px_6px_rgba(0,0,0,0.1),_0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_6px_rgba(0,0,0,0.3),_0_1px_3px_rgba(0,0,0,0.2)] ${currentTask === InterViewCurrentTask.YOU_SPEAKING
                ? "border-[1.5px] xs:border-[2px] border-[#08bfb1] animate-focus-pulse"
                : intervieweeTasks.includes(currentTask)
                  ? "border-1"
                  : ""
              }`}
          >
            {(interviewSetup?.conversationMode === ConversationMode.VIDEO && !isVideoOff && cameraAllowed) ? (
              <div className="w-full h-full flex justify-center items-center   overflow-hidden">
                <VideoPreview />
              </div>
            ) : user?.profilePicturePath ? (
              <div className="flex justify-center items-center w-full h-full">
                <img
                  src={user?.profilePicturePath || "./img1.png"}
                  alt="You"
                  style={{
                    filter: currentTask === InterViewCurrentTask.YOU_SPEAKING && !isMuted ? "blur(2px)" : "none",
                  }}
                  className="w-24 h-24 object-cover rounded-full"
                />
              </div>
            ) : (
              <span className={`w-24 h-24 max-w-[18vh] max-h-[18vh] rounded-full flex items-center justify-center text-[7vw] ${theme === "dark"
                ? "bg-[rgba(40,39,44,1)] text-gray-100"
                : "bg-[rgba(200,200,200,1)] text-gray-900"
                } aspect-ratio-[1/1]`}>
                {user?.firstName?.[0] || "U"}
              </span>
            )}
            <span className={`absolute p-1 text-xs rounded left-1 bottom-1 ${theme === "dark"
              ? "text-gray-100 bg-[rgba(0,0,0,0.5)]"
              : "text-gray-900 bg-[rgba(255,255,255,0.5)]"
              }`}>
              {user?.firstName || "You"}
            </span>
            {currentTask === InterViewCurrentTask.YOU_SPEAKING &&
              !isMuted &&
              (interviewSetup?.conversationMode === ConversationMode.VOICE || !!isVideoOff || !cameraAllowed) && <SoundWave />}
          </div>
        </div>
      </div>
    </>
  )
}

const bars = Array.from({ length: 15 }, (_, i) => i + 1)

const SoundWave = () => {
  const { theme } = useTheme()
  return (
    <div className="flex z-[2] items-center left-[50%] -translate-x-[50%] -translate-y-[50%] top-[50%] gap-0.5 h-[100px] absolute">
      {bars.map((bar) => (
        <motion.div
          key={bar}
          initial={{
            height: ["10%", "50%", "25%"][bar % 3],
          }}
          className={`w-1.5 rounded ${theme === "dark"
            ? "bg-accent-500/70"
            : "bg-accent-600/80"
            } shadow-[0_2px_4px_rgba(0,0,0,0.1)]`}
          animate={{
            height: ["10%", "50%", "25%"],
          }}
          transition={{
            opacity: "100%",
            duration: 0.8,
            repeat: Number.POSITIVE_INFINITY,
            repeatType: "mirror",
            ease: "easeInOut",
            delay: bar * 0.15,
          }}
        />
      ))}
    </div>
  )
}

interface TimerProps {
  time: number
  isRunning: boolean
  setTime: () => void
}

export const Timer = ({ time, setTime, isRunning }: TimerProps) => {
  const { theme } = useTheme()
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null
    if (isRunning) {
      timer = setInterval(() => {
        setTime()
      }, 1000)
    }
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [isRunning, setTime])

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = time % 60
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
  }

  return (
    <div className="text-center">
      <p className={`py-1.5 px-4 md:text-base text-xs text-nowrap text-white rounded-md transition-all duration-200 ${theme === "dark"
        ? "bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 border-accent-700/40 border"
        : "bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 border-accent-700/40 border"
        } shadow-[0_2px_4px_rgba(0,0,0,0.1)] hover:shadow-[0_3px_6px_rgba(0,0,0,0.15)]`}>
        {formatTime(time)}
      </p>
    </div>
  )
}

export const MainTimer = () => {
  const { interview } = useInterview()
  const { theme } = useTheme()
  const { setUp } = interview
  const [time, setTime] = useState(0)

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null

    if (setUp?.started) {
      timer = setInterval(() => {
        setTime((prev) => prev + 1)
      }, 1000)
    }

    return () => {
      if (timer) clearInterval(timer)
    }
  }, [setUp?.started])

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = time % 60
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
  }

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0 xs:px-3 xs:py-0.5 sm:px-4 sm:py-0.5 rounded-full transition-all duration-200 ${theme === "dark"
      ? "bg-[rgba(46,45,50,1)] "
      : "bg-[rgba(247,248,250,1)] "
      } `}>
      <Clock className="w-3 h-3 xs:w-3.5  sm:w-4  text-accent-600" />
      <span className={`font-mono pt-0.5 text-[10px] xs:text-xs sm:text-sm font-semibold ${theme === "dark" ? "text-gray-100" : "text-gray-900"
        }`} aria-live="polite">
        {formatTime(time)}
      </span>
    </div>
  )
}

export default InterviewCard